import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Content from "../../layout/content/Content";
import {
    Card,
    Badge,
    Row,
    Col,
    Button,
    Spinner,
} from "reactstrap";
import Head from "../../layout/head/Head";
import {
    BlockBetween,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    Icon,
} from "../../components/Component";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import {
    TableColumnsType,
    Table,
    Timeline,
    Collapse as AntCollapse,
    Flex,
    Spin,
    Popover,
} from "antd";
import inventoryCreation from "../../api/inventory/inventoryCreation";
import dayjs from "dayjs";
import { UtilizationTaskPanel } from "./UtilizationTaskPanel";
const { Panel } = AntCollapse;

const SEGMENT_TO_UNIT_LABEL: Record<string, string> = {
    Tower: "Tower / Block",
    Villa: "Villa",
    Floor: "Tower / Block",
    Flat: "Flat",
};
const DEFAULT_UNIT_LABEL = "Tower / Block";

const MaterialUtilizationListDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [detailsData, setDetailsData] = useState<any>();
    const [isStrictMode, setIsStrictMode] = useState<boolean>(false);
    const [approverIds, setApproverIds] = useState<any>([]);

    const signatureFromApi = detailsData?.dms?.find(
        (doc: any) => doc.moduleFileType === "Signature"
    );

    const headerInfo = detailsData?.header;
    const normalizedSegmentLevel = headerInfo?.segmentLevel &&
        headerInfo.segmentLevel.toString().toLowerCase() !== "null"
        ? headerInfo.segmentLevel
        : undefined;
    const segmentLevelDisplay = normalizedSegmentLevel || "NA";
    const hierarchyUnitLabel = (normalizedSegmentLevel && SEGMENT_TO_UNIT_LABEL[normalizedSegmentLevel]) || DEFAULT_UNIT_LABEL || "NA";
    const towerUnitDisplay = headerInfo?.siteStructureUnitName ||
        headerInfo?.blockName ||
        headerInfo?.towerBlockVillaLabel ||
        "NA";
    const floorDisplay = headerInfo?.floorNo !== null &&
        headerInfo?.floorNo !== undefined &&
        headerInfo?.floorNo !== ""
        ? headerInfo.floorNo
        : "NA";
    const flatDisplay = headerInfo?.siteStructureSubUnitName ||
        headerInfo?.flatLabel ||
        headerInfo?.flatName ||
        "NA";
    const locationDisplay = headerInfo?.locationName || headerInfo?.locationLabel || "NA";

    useEffect(() => {
        if (id) {
            fetchHandoverDetails(id);
        }
    }, [id]);

    const fetchHandoverDetails = async (handoverId: any) => {
        setIsLoading(true);
        try {
            const response = await inventoryCreation.fetchHandOverById(handoverId);
            const { response: _response, status } = response;
            if (status) {
                const raw = _response.data;
                const normalizedData = {
                    header: {
                        handoverId: raw.handoverId,
                        handoverNumber: raw.handoverNumber,
                        issueNumber: raw.issueNumber,
                        siteId: raw.siteId,
                        siteName: raw.siteName,
                        createdAt: raw.createdAt,
                        createdUserName: raw.createdUserName,
                        issueStatus: raw.utilizationStatus,
                        handoverStatus: raw.handoverStatus,
                        isStrictApproval: raw.isStrictApproval ?? false,
                        segmentLevel: raw.segmentLevel,
                        siteStructureUnitName: raw.siteStructureUnitName,
                        blockName: raw.blockName,
                        towerBlockVillaLabel: raw.towerBlockVillaLabel,
                        floorNo: raw.floorNo,
                        siteStructureSubUnitName: raw.siteStructureSubUnitName,
                        flatLabel: raw.flatLabel,
                        flatName: raw.flatName,
                        locationName: raw.locationName,
                        locationLabel: raw.locationLabel,
                        approvalRequired: raw.approvalRequired,
                        handoverToName: raw.handoverToName,
                        utilizationStatus: raw.utilizationStatus,
                        handoverDate: raw.handoverDate,
                    },
                    lines: raw.handoverLines || [],
                    approvals: raw.approvals || [],
                    dms: raw.dms || [],
                };

                const isStrict = normalizedData?.header?.isStrictApproval === true || normalizedData?.header?.isStrictApproval === 1;
                setIsStrictMode(isStrict);
                setDetailsData(normalizedData);

                if (normalizedData?.approvals) {
                    const processedApprovals = normalizedData.approvals.map((approval: any) => ({
                        ...approval,
                        level: approval.level?.toLowerCase() || approval.level,
                        status: approval.status?.toLowerCase() || approval.status,
                    }));

                    const ids = processedApprovals
                        .filter((item: any) => item?.status !== "approved")
                        .map((item: any) => item?.userId);

                    setApproverIds(ids);
                }
            }
        } catch (error) {
            console.error("Error fetching handover details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getColumns = () => {
        const baseColumns: TableColumnsType<any> = [
            {
                title: "Material Category",
                dataIndex: "categoryName",
                key: "categoryName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "Material Name",
                dataIndex: "materialName",
                key: "materialName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "UOM",
                dataIndex: "uomName",
                key: "uomName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "Utilization Status",
                dataIndex: "utilizationStatus",
                key: "utilizationStatus",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "Handovered Quantity",
                dataIndex: "quantityHandovered",
                key: "quantityHandovered",
                width: 200,
                render: (text) => <span>{text}</span>,
            },
            {
                title: "Utilized Quantity",
                dataIndex: "quantityUtilized",
                key: "quantityUtilized",
                width: 200,
                render: (text) => <span>{text}</span>,
            },
            {
                title: "Returned Quantity",
                dataIndex: "quantityReturned",
                key: "quantityReturned",
                width: 200,
                render: (text) => <span>{text}</span>,
            },
            {
                title: "Damaged Quantity",
                dataIndex: "quantityDamaged",
                key: "quantityDamaged",
                width: 200,
                render: (text) => <span>{text}</span>,
            },
        ];

        return baseColumns;
    };

    return (
        <>
            <Head title="Utilization Details" />
            <Content>
                <Card className="card-bordered">
                    <div className="card-aside-wrap">
                        {isLoading ? (
                            <Flex align="center" gap="middle" className="pageLoader">
                                <Spin size="large" className="m-auto" />
                            </Flex>
                        ) : (
                            <div className="card-inner card-inner-lg">
                                <BlockHead size="lg">
                                    <BlockBetween>
                                        <BlockHeadContent>
                                            <BlockTitle tag="h4">
                                                <span>Utilization Details # - {detailsData?.header?.handoverNumber} | {detailsData?.header?.utilizationStatus}</span>
                                            </BlockTitle>
                                        </BlockHeadContent>
                                        <div className="d-flex">
                                            <div className="paddingLeft10">
                                                <Button className="toggle" color="primary" onClick={() => navigate("/material-utilization")}>
                                                    <Icon name="arrow-left" />
                                                    <span>Back</span>
                                                </Button>
                                            </div>
                                        </div>
                                    </BlockBetween>
                                </BlockHead>
                                <div className="content clearfix">
                                    <AntCollapse defaultActiveKey={["1", "2"]}>
                                        <Panel header="General Information" key="1">
                                            <div>
                                                <Col className="justify-start w-100 flex-wrap p-3">
                                                    <div className="w-50 border-end">
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Site</div>
                                                            <div className="data-value w-70">{detailsData?.header?.siteName || "NA"}</div>
                                                        </div>
                                                        {segmentLevelDisplay !== "NA" && (
                                                            <div className="data-col align-center pt-1">
                                                                <div className="data-label w-30">Site Hierarchy</div>
                                                                <div className="data-value w-70">{segmentLevelDisplay}</div>
                                                            </div>
                                                        )}
                                                        {towerUnitDisplay && towerUnitDisplay !== "NA" && (
                                                            <div className="data-col align-center pt-1">
                                                                <div className="data-label w-30">{hierarchyUnitLabel}</div>
                                                                <div className="data-value w-70">{towerUnitDisplay}</div>
                                                            </div>
                                                        )}
                                                        {floorDisplay && (
                                                            <div className="data-col align-center pt-1">
                                                                <div className="data-label w-30">Floor</div>
                                                                <div className="data-value w-70">{floorDisplay}</div>
                                                            </div>
                                                        )}
                                                        {flatDisplay && (
                                                            <div className="data-col align-center pt-1">
                                                                <div className="data-label w-30">Flat</div>
                                                                <div className="data-value w-70">{flatDisplay}</div>
                                                            </div>
                                                        )}
                                                        {locationDisplay && locationDisplay !== "NA" && (
                                                            <div className="data-col align-center pt-1">
                                                                <div className="data-label w-30">Location</div>
                                                                <div className="data-value w-70">{locationDisplay}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="w-50 paddingLeft10">
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Utilization Status</div>
                                                            <div className="data-value w-70">{detailsData?.header?.utilizationStatus || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Issue Number</div>
                                                            <div className="data-value w-70">{detailsData?.header?.issueNumber || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Handover Number</div>
                                                            <div className="data-value w-70">{detailsData?.header?.handoverNumber || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Handover To</div>
                                                            <div className="data-value w-70">{detailsData?.header?.handoverToName || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Creation Date</div>
                                                            <div className="data-value w-70">
                                                                {dayjs(detailsData?.header?.createdAt).format("DD-MMM-YYYY") || "NA"}
                                                            </div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Created By</div>
                                                            <div className="data-value w-70">{detailsData?.header?.createdUserName || "NA"}</div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </div>
                                        </Panel>
                                        <Panel header="Material Information" key="2">
                                            <div className="table-responsive poTable">
                                                <div style={{ margin: "0px auto 15px auto" }}>
                                                    <Table
                                                        className="customTable"
                                                        columns={getColumns()}
                                                        dataSource={detailsData?.lines?.map((item: any) => ({ ...item, key: item?.issueLineId || item?.handoverLineId }))}
                                                        scroll={{ x: 1000 }}
                                                        bordered
                                                        size="middle"
                                                        pagination={false}
                                                        expandable={{
                                                            expandedRowRender: (record: any) => (
                                                                <UtilizationTaskPanel
                                                                    tasks={Array.isArray(record?.tasks) ? record.tasks : []}
                                                                    readOnly
                                                                />
                                                            ),
                                                            rowExpandable: () => true,
                                                        }}
                                                    />
                                                </div>
                                                {signatureFromApi && (
                                                    <Row className="m-4">
                                                        <Col className="text-end">
                                                            <div className="signatureView" style={{
                                                                display: 'inline-block',
                                                                textAlign: 'left',
                                                                // border: '1px solid #e5ecf7',
                                                                padding: '15px',
                                                                borderRadius: '5px',
                                                                // backgroundColor: '#f7faff',
                                                                maxWidth: '300px'
                                                            }}>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <strong style={{ marginBottom: '10px' }}>Signature</strong>
                                                                    <img
                                                                        src={signatureFromApi.filePath}
                                                                        alt="Signature"
                                                                        style={{
                                                                            width: "160px",
                                                                            height: "100px",
                                                                            objectFit: 'contain',
                                                                            // border: '1px solid #ddd',
                                                                            borderRadius: '4px'
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </div>
                                        </Panel>
                                    </AntCollapse>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            </Content>
        </>
    );
};

export default MaterialUtilizationListDetails;